import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useUpdateLocale } from "./use-locales";
import { LOCALE_DISPLAY_NAME_MAX, type SupportedLocale } from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

interface Props {
  readonly target: SupportedLocale | null;
  readonly onOpenChange: (open: boolean) => void;
}

export function EditLocaleDialog({ target, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateLocale();

  useEffect(() => {
    if (target) {
      setDisplayName(target.displayName);
      setError(null);
    }
  }, [target]);

  const trimmed = displayName.trim();
  const isDirty = target != null && trimmed !== target.displayName;
  const isValid = trimmed.length > 0 && trimmed.length <= LOCALE_DISPLAY_NAME_MAX;

  const handleSubmit = async () => {
    if (!target || !isValid || !isDirty || update.isPending) return;
    setError(null);
    try {
      // We deliberately do NOT send `isCanonical` here. Promotion is a
      // separate explicit action on the list row (`Promote to canonical`
      // button) so an unrelated edit can never trip the canonical swap
      // by accident.
      await update.mutateAsync({
        code: target.code,
        input: { displayName: trimmed },
      });
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <Dialog open={target != null} onOpenChange={(next) => !update.isPending && onOpenChange(next)}>
      <DialogContent data-testid="compliance-edit-locale-dialog">
        <DialogHeader>
          <DialogTitle>{t("compliance.locales.edit.title")}</DialogTitle>
          <DialogDescription>
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{target?.code}</code>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="locale-edit-display-name">
              {t("compliance.locales.fields.displayName")}
            </Label>
            <Input
              id="locale-edit-display-name"
              data-testid="compliance-edit-locale-display-name-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={LOCALE_DISPLAY_NAME_MAX}
            />
          </div>

          {error && (
            <p data-testid="compliance-edit-locale-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            data-testid="compliance-edit-locale-submit"
            onClick={handleSubmit}
            disabled={!isValid || !isDirty || update.isPending}
          >
            {update.isPending ? t("compliance.actions.saving") : t("compliance.actions.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
